/**
 * CheckoutSheet — Telr iframe checkout (ivp_iframe=3 per Telr support)
 *
 * Card/Mada/Visa  → Telr hosted page embedded in iframe
 * Apple Pay       → native ApplePaySession if browser supports it
 * Apple Pay (no support) → QR code + polling
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useToast } from '../../context/ToastContext';
import visaLogo       from '../../../assets/payment/visa-logo.svg';
import madaLogo       from '../../../assets/payment/mada-logo.svg';
import mastercardLogo from '../../../assets/payment/mastercard-logo.svg';
import applePayLogo   from '../../../assets/payment/apple-pay-logo.svg';
import SAR            from '../../../components/SAR';

// ── Feature detection ────────────────────────────────────────────────────────
const CAN_AP = (() => {
  try { return !!(window.ApplePaySession && window.ApplePaySession.canMakePayments()); }
  catch { return false; }
})();

// ── QR helper (no external lib needed) ───────────────────────────────────────
function QRImg({ url, size = 200 }) {
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}&bgcolor=ffffff&color=0f172a&margin=10`;
  return <img src={src} alt="QR للدفع" width={size} height={size} className="rounded-2xl shadow-lg border border-gray-100" />;
}

const QUICK = [50, 100, 200, 500];

// ── Main component ───────────────────────────────────────────────────────────
export default function CheckoutSheet({ open, onClose, initialAmount = '', onSuccess }) {
  const { apiCall } = useCustomerAuth();
  const toast       = useToast();
  const pollRef     = useRef(null);

  // step: 'form' | 'iframe' | 'apple-pay' | 'qr' | 'success' | 'fail'
  const [step, setStep]         = useState('form');
  const [method, setMethod]     = useState('card'); // 'card' | 'apple'
  const [amount, setAmount]     = useState(String(initialAmount || ''));
  const [billing, setBilling]   = useState({ name: '', phone: '', email: '' });
  const [billOpen, setBillOpen] = useState(false);
  const [session, setSession]   = useState(null);
  const [creating, setCreating] = useState(false);
  const [apLoading, setApLoading] = useState(false);
  const [newBalance, setNewBalance] = useState(null);
  const [polling, setPolling]   = useState(false);


  // ── Reset on open/close ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open) { stopPolling(); return; }
    setStep('form'); setMethod('card'); setSession(null);
    setNewBalance(null); setPolling(false);
    setAmount(String(initialAmount || ''));
    apiCall('/customer/auth/me')
      .then(r => setBilling({ name: r.display_name || '', phone: r.phone || '', email: r.email || '' }))
      .catch(() => {});
  }, [open]); // eslint-disable-line

  // ── postMessage listener (Telr iframe → CheckoutSheet) ───────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type !== 'TELR_PAYMENT') return;
      const { result, new_balance } = e.data;
      if (result === 'success') {
        setNewBalance(new_balance ?? null);
        setStep('success');
        if (onSuccess) onSuccess(new_balance);
      } else if (result === 'cancelled') {
        setStep('form');
      } else {
        setStep('fail');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onSuccess]); // eslint-disable-line

  // ── Polling (for QR mode — calls full verify every 5s) ───────────────────
  const stopPolling = useCallback(() => {
    clearInterval(pollRef.current);
    setPolling(false);
  }, []);

  const startPolling = useCallback((reqId) => {
    setPolling(true);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      if (++attempts > 36) { stopPolling(); return; } // max 3 min
      try {
        const r = await apiCall(`/customer/payment/topup/verify?reqid=${encodeURIComponent(reqId)}`, { method: 'POST' });
        if (r.paid) {
          stopPolling();
          setNewBalance(r.new_balance ?? r.balance ?? null);
          setStep('success');
          if (onSuccess) onSuccess(r.new_balance ?? r.balance);
        }
      } catch { /* silent */ }
    }, 5000);
  }, [apiCall, onSuccess, stopPolling]);

  // ── Telr payment iframe state ─────────────────────────────────────────────
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const iframeRef = useRef(null);

  // Poll backend to detect payment completion while iframe is open
  const iframePollRef = useRef(null);
  const startIframePoll = useCallback((requestId) => {
    clearInterval(iframePollRef.current);
    iframePollRef.current = setInterval(async () => {
      try {
        const r = await apiCall(`/customer/payment/topup/poll/${requestId}`);
        if (r.paid) {
          clearInterval(iframePollRef.current);
          // verify to credit wallet + get new balance
          try {
            const v = await apiCall(`/customer/payment/topup/verify?reqid=${requestId}`, { method: 'POST' });
            setNewBalance(v.new_balance ?? null);
            if (onSuccess) onSuccess(v.new_balance);
          } catch { /* already credited — still show success */ }
          setStep('success');
        }
      } catch { /* silent */ }
    }, 2500);
  }, [apiCall, onSuccess]);

  useEffect(() => () => clearInterval(iframePollRef.current), []);

  // ── Create Telr session (card popup + Apple Pay) ────────────────────────────
  const createSession = async (chosenMethod) => {
    const amt = parseFloat(amount);
    if (!amt || amt < 10) { toast.error('الحد الأدنى 10 ر.س'); return; }
    setCreating(true);
    try {
      const res = await apiCall('/customer/payment/topup/init', {
        method: 'POST',
        body: JSON.stringify({
          amount:        amt,
          billing_name:  billing.name  || undefined,
          billing_email: billing.email || undefined,
          billing_phone: billing.phone || undefined,
          origin_path:   window.location.pathname + window.location.search,
        }),
      });
      setSession(res);
      if (chosenMethod === 'card') {
        setStep('iframe');
        setTimeout(() => { window.location.href = res.payment_url; }, 800);
      } else if (chosenMethod === 'apple') {
        if (CAN_AP) { setStep('apple-pay'); }
        else { setStep('qr'); startPolling(res.request_id); }
      }
    } catch (err) {
      toast.error(err.message || 'تعذر إنشاء جلسة الدفع');
    } finally { setCreating(false); }
  };

  // ── Apple Pay native flow ─────────────────────────────────────────────────
  const triggerApplePay = useCallback(async () => {
    if (!session) return;
    const amt = parseFloat(amount);
    setApLoading(true);

    const apSession = new window.ApplePaySession(3, {
      countryCode: 'SA', currencyCode: 'SAR',
      supportedNetworks: ['visa', 'masterCard', 'mada'],
      merchantCapabilities: ['supports3DS'],
      total: { label: 'وترلي', amount: amt.toFixed(2) },
    });

    apSession.onvalidatemerchant = async (event) => {
      try {
        const ms = await apiCall('/customer/payment/apple-pay/validate-session', {
          method: 'POST', body: JSON.stringify({ validation_url: event.validationURL }),
        });
        apSession.completeMerchantValidation(ms);
      } catch { apSession.abort(); setApLoading(false); toast.error('تعذر التحقق من Apple Pay'); }
    };

    apSession.onpaymentauthorized = async (event) => {
      try {
        const res = await apiCall('/customer/payment/apple-pay/process', {
          method: 'POST',
          body: JSON.stringify({ payment_token: event.payment.token, amount: amt, req_id: session.request_id }),
        });
        apSession.completePayment({ status: res.paid ? window.ApplePaySession.STATUS_SUCCESS : window.ApplePaySession.STATUS_FAILURE });
        setApLoading(false);
        if (res.paid) { setNewBalance(res.new_balance ?? null); setStep('success'); if (onSuccess) onSuccess(res.new_balance); }
        else { toast.error('فشل الدفع'); }
      } catch {
        apSession.completePayment({ status: window.ApplePaySession.STATUS_FAILURE });
        setApLoading(false);
        toast.error('خطأ في معالجة الدفع');
      }
    };

    apSession.oncancel = () => setApLoading(false);
    apSession.begin();
  }, [session, amount, apiCall, onSuccess, toast]);

  // ── Lock body scroll when sheet is open ──────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    const prevPos = document.body.style.position;
    const prevTop = document.body.style.top;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = prev;
      document.body.style.position = prevPos;
      document.body.style.top = prevTop;
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  // ── Hide bottom nav when sheet is open ───────────────────────────────────
  useEffect(() => {
    const nav = document.querySelector('nav.lg\\:hidden.fixed.bottom-4');
    const header = document.querySelector('header.lg\\:hidden');
    if (!nav || !header) return;
    // snapshot original values before we touch them
    const prevNavTransform    = nav.style.transform;
    const prevNavOpacity      = nav.style.opacity;
    const prevNavPointer      = nav.style.pointerEvents;
    const prevHeaderTransform = header.style.transform;
    const prevHeaderOpacity   = header.style.opacity;
    const prevHeaderPointer   = header.style.pointerEvents;
    if (open) {
      nav.style.transform = 'translateY(120px)';
      nav.style.opacity = '0';
      nav.style.pointerEvents = 'none';
      header.style.transform = 'translateY(-100%)';
      header.style.opacity = '0';
      header.style.pointerEvents = 'none';
    }
    return () => {
      if (open) {
        nav.style.transform    = prevNavTransform;
        nav.style.opacity      = prevNavOpacity;
        nav.style.pointerEvents= prevNavPointer;
        header.style.transform = prevHeaderTransform;
        header.style.opacity   = prevHeaderOpacity;
        header.style.pointerEvents = prevHeaderPointer;
      }
    };
  }, [open]);

  const [visible, setVisible] = React.useState(false);
  const [closing, setClosing] = React.useState(false);

  // ── Drag-to-dismiss ──────────────────────────────────────────────────────
  const dragY     = useRef(0);
  const dragStart = useRef(0);
  const sheetRef  = useRef(null);
  const isDragging = useRef(false);

  const handleTouchStart = useCallback((e) => {
    dragStart.current = e.touches[0].clientY;
    dragY.current = 0;
    isDragging.current = true;
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging.current) return;
    const dy = e.touches[0].clientY - dragStart.current;
    dragY.current = dy;
    if (dy < 0) {
      // شد للأعلى — resistance: يتحرك 20% فقط من المسافة
      const r = dy * 0.18;
      if (sheetRef.current) {
        sheetRef.current.style.transition = 'none';
        sheetRef.current.style.transform = `translateY(${r}px)`;
      }
    } else {
      // شد للأسفل — يتبع الإصبع
      if (sheetRef.current) {
        sheetRef.current.style.transition = 'none';
        sheetRef.current.style.transform = `translateY(${dy}px)`;
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const dy = dragY.current;
    if (dy > 100) {
      // أُسدل أكثر من 100px → أغلق
      onClose();
    } else {
      // ارجع لمكانك بسلاسة
      if (sheetRef.current) {
        sheetRef.current.style.transition = 'transform 0.35s cubic-bezier(0.34,1.2,0.64,1)';
        sheetRef.current.style.transform = 'translateY(0)';
        setTimeout(() => {
          if (sheetRef.current) sheetRef.current.style.transition = '';
        }, 360);
      }
    }
  }, [onClose]);

  React.useEffect(() => {
    if (open) {
      setClosing(false);
      setVisible(true);
    } else if (visible) {
      setClosing(true);
      const t = setTimeout(() => { setVisible(false); setClosing(false); }, 320);
      return () => clearTimeout(t);
    }
  }, [open]); // eslint-disable-line

  if (!visible) return null;
  const amt = parseFloat(amount) || 0;

  const S = {
    bg:      'var(--wt-surface, #0d2035)',
    border:  'var(--wt-border, rgba(255,255,255,0.08))',
    text:    'var(--wt-text-primary, #e2f0ff)',
    muted:   'var(--wt-text-muted, rgba(186,230,253,0.5))',
    input:   'var(--wt-surface, rgba(255,255,255,0.05))',
  };

  const btnBase = {
    width:'100%', padding:'0.9rem', borderRadius:'0.875rem', border:'none',
    fontSize:'0.9rem', fontWeight:800, cursor:'pointer',
    fontFamily:"'Cairo',sans-serif", display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem',
    transition:'opacity 0.15s, transform 0.15s',
  };

  const iconBtn = {
    width:34, height:34, borderRadius:'50%', border:'none', cursor:'pointer', flexShrink:0,
    background:'rgba(255,255,255,0.07)', display:'flex', alignItems:'center', justifyContent:'center',
    color:'var(--wt-text-muted)', fontSize:'1.1rem',
  };

  const inputSt = {
    width:'100%', padding:'0.75rem 0.9rem', borderRadius:'0.75rem',
    background:'rgba(255,255,255,0.05)', border:'1.5px solid var(--wt-border)',
    color: S.text, fontSize:'0.88rem', fontWeight:600,
    fontFamily:"'Cairo',sans-serif", outline:'none', boxSizing:'border-box',
  };

  // ═════════════════════════════════════════════════════════════════════════
  const content = (
    <>
      <style>{`
        @keyframes cs-overlay-in  { from{opacity:0} to{opacity:1} }
        @keyframes cs-overlay-out { from{opacity:1} to{opacity:0} }
        @keyframes cs-sheet-in    { from{transform:translateY(100%)} to{transform:translateY(0)} }
        @keyframes cs-sheet-out   { from{transform:translateY(0)}    to{transform:translateY(100%)} }
        @keyframes cs-spin        { to{transform:rotate(360deg)} }
        @keyframes cs-ping        { 75%,100%{transform:scale(2);opacity:0} }
        .cs-input:focus { border-color:rgba(10,186,181,0.7) !important; box-shadow:0 0 0 3px rgba(10,186,181,0.12) !important; }
        .cs-input::placeholder { opacity:0.35; }
        .cs-quick:active { transform:scale(0.95); }
        .cs-cta:not(:disabled):active { transform:scale(0.98); }
        .cs-cta:disabled { opacity:0.4; cursor:not-allowed; }
        .cs-overlay { height: 100vh; height: 100dvh; }
      `}</style>

      {/* Container — يغطي كامل الشاشة دائماً بغض النظر عن حركة الـ sheet */}
      <div
        className="cs-overlay"
        onClick={onClose}
        onTouchMove={e => e.preventDefault()}
        style={{
          position:'fixed', top:0, left:0, right:0, bottom:0,
          width:'100vw', minHeight:'-webkit-fill-available',
          zIndex:9998,
          background:'rgba(0,0,0,0.7)',
          animation: closing ? 'cs-overlay-out 0.30s ease forwards' : 'cs-overlay-in 0.22s ease',
          display:'flex', flexDirection:'column', justifyContent:'flex-end',
        }}
      >
      {/* Sheet — يتحرك هنا داخل الـ overlay ليبقى مغطياً دائماً */}
      <div
        ref={sheetRef}
        onClick={e => e.stopPropagation()}
        onTouchMove={e => e.stopPropagation()}
        style={{
          position:'relative',
          width:'100%', maxHeight:'92vh', overflowY:'auto',
          overscrollBehavior:'contain',
          WebkitOverflowScrolling:'touch',
          background:'var(--wt-surface, #0b1e30)',
          borderRadius:'1.375rem 1.375rem 0 0',
          boxShadow:'0 -16px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(10,186,181,0.12)',
          willChange:'transform',
          animation: closing
            ? 'cs-sheet-out 0.30s cubic-bezier(0.4,0,1,1) forwards'
            : 'cs-sheet-in 0.38s cubic-bezier(0.34,1.1,0.64,1)',
        }}
      >
        {/* Drag handle — touch to dismiss */}
        <div
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ display:'flex', justifyContent:'center', padding:'0.9rem 0 0.3rem', cursor:'grab', userSelect:'none', touchAction:'none' }}
        >
          <div style={{ width:40, height:5, borderRadius:999, background:'rgba(255,255,255,0.18)', transition:'background 0.2s' }}/>
        </div>

        {/* Top accent */}
        <div style={{ height:2, background:'linear-gradient(90deg,transparent,#0ABAB5 40%,rgba(56,189,248,0.7),transparent)', margin:'0 1rem', borderRadius:999 }}/>

        {/* ══ STEP: FORM ══ */}
        {step === 'form' && (
          <div style={{ padding:'1rem 1.1rem 2.5rem', fontFamily:"'Cairo',sans-serif" }}>

            {/* Header */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.25rem', marginTop:'0.25rem' }}>
              <div>
                <div style={{ fontSize:'0.62rem', color:'rgba(10,186,181,0.6)', fontWeight:700, letterSpacing:'0.1em' }}>WTRLY</div>
                <div style={{ fontSize:'1.15rem', fontWeight:900, color: S.text, lineHeight:1.1 }}>شحن المحفظة</div>
              </div>
              <button onClick={onClose} style={iconBtn}>✕</button>
            </div>

            {/* Quick amounts */}
            <div style={{ fontSize:'0.7rem', fontWeight:700, color: S.muted, marginBottom:'0.6rem' }}>اختر مبلغاً</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'0.5rem', marginBottom:'0.75rem' }}>
              {QUICK.map(q => {
                const isAct = parseFloat(amount) === q;
                return (
                  <button key={q} className="cs-quick" onClick={() => setAmount(String(q))} style={{
                    padding:'0.75rem 0', borderRadius:'0.75rem', border:'none', cursor:'pointer',
                    fontSize:'0.88rem', fontWeight:800, fontFamily:"'Cairo',sans-serif",
                    background: isAct ? 'linear-gradient(135deg,#0ABAB5,#0891b2)' : 'rgba(255,255,255,0.06)',
                    color: isAct ? '#fff' : S.text,
                    boxShadow: isAct ? '0 4px 14px rgba(10,186,181,0.35)' : 'none',
                    border: isAct ? 'none' : '1px solid var(--wt-border)',
                    transition:'all 0.15s',
                  }}>{q}</button>
                );
              })}
            </div>

            {/* Custom input */}
            <div style={{ position:'relative', marginBottom:'1.1rem' }}>
              <input
                className="cs-input"
                type="number" inputMode="decimal" value={amount} min="10"
                onChange={e => setAmount(e.target.value)}
                placeholder="مبلغ مخصص"
                style={{ ...inputSt, textAlign:'right', fontSize:'1.1rem', padding:'0.8rem 1rem 0.8rem 3rem' }}
              />
              <SAR style={{ position:'absolute', left:'0.875rem', top:'50%', transform:'translateY(-50%)', height:'0.85em', width:'auto', color: S.muted }} />
            </div>

            {/* Method */}
            <div style={{ fontSize:'0.7rem', fontWeight:700, color: S.muted, marginBottom:'0.6rem' }}>طريقة الدفع</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem', marginBottom:'1rem' }}>
              {/* Card */}
              {[
                { key:'card',  label:'بطاقة / مدى',  sub:'Visa · Mastercard · مدى', logos: [visaLogo, madaLogo, mastercardLogo], isApple:false },
                { key:'apple', label:'Apple Pay',      sub: CAN_AP ? 'متاح على هذا الجهاز' : 'QR للجوال',   logos: [applePayLogo], isApple:true },
              ].map(m => {
                const isAct = method === m.key;
                const accentColor = m.isApple ? '#ffffff' : '#0ABAB5';
                return (
                  <button key={m.key} onClick={() => setMethod(m.key)} style={{
                    position:'relative', display:'flex', flexDirection:'column', alignItems:'center', gap:'0.45rem',
                    padding:'0.875rem 0.5rem', borderRadius:'0.875rem', cursor:'pointer',
                    background: isAct
                      ? (m.isApple ? 'rgba(255,255,255,0.08)' : 'rgba(10,186,181,0.1)')
                      : 'rgba(255,255,255,0.04)',
                    border: `1.5px solid ${isAct ? (m.isApple ? 'rgba(255,255,255,0.35)' : 'rgba(10,186,181,0.55)') : 'var(--wt-border)'}`,
                    boxShadow: isAct ? `0 0 16px ${m.isApple ? 'rgba(255,255,255,0.08)' : 'rgba(10,186,181,0.15)'}` : 'none',
                    transition:'all 0.18s',
                  }}>
                    {isAct && (
                      <div style={{ position:'absolute', top:'0.4rem', left:'0.4rem', width:16, height:16, borderRadius:'50%', background: m.isApple ? '#fff' : '#0ABAB5', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.5rem', color: m.isApple ? '#000' : '#fff', fontWeight:900 }}>✓</div>
                    )}
                    <div style={{ width:38, height:38, borderRadius:'0.75rem', display:'flex', alignItems:'center', justifyContent:'center', background: isAct ? (m.isApple ? 'rgba(255,255,255,0.12)' : 'rgba(10,186,181,0.15)') : 'rgba(255,255,255,0.06)' }}>
                      {m.isApple
                        ? <img src={applePayLogo} alt="Apple Pay" style={{ height:20, filter:'invert(1)' }}/>
                        : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="1.8"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>
                      }
                    </div>
                    <div style={{ textAlign:'center' }}>
                      <div style={{ fontSize:'0.75rem', fontWeight:800, color: isAct ? (m.isApple ? '#fff' : '#0ABAB5') : S.text }}>{m.label}</div>
                      <div style={{ fontSize:'0.6rem', color: S.muted, marginTop:'0.1rem' }}>{m.sub}</div>
                    </div>
                    {!m.isApple && (
                      <div style={{ display:'flex', gap:'0.3rem', alignItems:'center' }}>
                        {m.logos.map((l,i) => <img key={i} src={l} alt="" style={{ height:10, opacity:isAct?1:0.5 }}/>)}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Billing accordion */}
            <button onClick={() => setBillOpen(p => !p)} style={{
              width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'0.65rem 0.875rem', borderRadius:'0.75rem', border:'1px solid var(--wt-border)',
              background:'rgba(255,255,255,0.04)', cursor:'pointer', marginBottom: billOpen ? 0 : '0.75rem',
              fontFamily:"'Cairo',sans-serif",
            }}>
              <span style={{ display:'flex', alignItems:'center', gap:'0.5rem', fontSize:'0.78rem', fontWeight:700, color: S.text }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                بيانات الفاتورة
                {billing.name && <span style={{ fontSize:'0.65rem', color: S.muted, fontWeight:400 }}>({billing.name})</span>}
              </span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: S.muted, transform: billOpen ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}><path d="M6 9l6 6 6-6"/></svg>
            </button>
            {billOpen && (
              <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid var(--wt-border)', borderTop:'none', borderRadius:'0 0 0.75rem 0.75rem', padding:'0.75rem', marginBottom:'0.75rem', display:'flex', flexDirection:'column', gap:'0.6rem' }}>
                {[['name','الاسم','text'],['phone','الجوال','tel'],['email','البريد','email']].map(([k,l,t]) => (
                  <div key={k}>
                    <label style={{ display:'block', fontSize:'0.65rem', fontWeight:700, color: S.muted, marginBottom:'0.3rem' }}>{l}</label>
                    <input className="cs-input" type={t} value={billing[k]} placeholder={l}
                      onChange={e => setBilling(p => ({...p,[k]:e.target.value}))}
                      style={inputSt}/>
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            {amt >= 10 && (
              <div style={{
                display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'0.75rem 0.875rem', borderRadius:'0.875rem', marginBottom:'0.75rem',
                background:'rgba(10,186,181,0.08)', border:'1px solid rgba(10,186,181,0.2)',
              }}>
                <span style={{ fontSize:'0.78rem', color: S.muted }}>إجمالي الشحن</span>
                <span style={{ fontSize:'1.35rem', fontWeight:900, color:'#0ABAB5', display:'flex', alignItems:'center', gap:'0.25rem', direction:'ltr' }}><SAR style={{ height:'0.85em', width:'auto' }}/>{amt.toFixed(2)}</span>
              </div>
            )}

            {/* CTA */}
            <button className="cs-cta" onClick={() => createSession(method)} disabled={creating || amt < 10}
              style={{ ...btnBase, background:'linear-gradient(135deg,#0ABAB5,#0891b2)', color:'#fff', boxShadow:'0 6px 22px rgba(10,186,181,0.35)' }}>
              {creating
                ? <span style={{ width:18, height:18, borderRadius:'50%', border:'2.5px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'cs-spin 0.7s linear infinite', display:'inline-block' }}/>
                : <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                    {method === 'apple' && !CAN_AP ? 'عرض QR للدفع' : 'ادفع الآن'} — <SAR style={{ height:'0.9em', width:'auto' }}/>{amt.toFixed(2)}
                  </>}
            </button>
            <p style={{ textAlign:'center', fontSize:'0.62rem', color: S.muted, marginTop:'0.6rem', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.3rem' }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              مدفوعات آمنة ومشفرة عبر Telr · PCI DSS Level 1
            </p>
          </div>
        )}

        {/* ══ STEP: REDIRECTING ══ */}
        {step === 'iframe' && (
          <div style={{ padding:'3rem 1.5rem', fontFamily:"'Cairo',sans-serif", display:'flex', flexDirection:'column', alignItems:'center', gap:'1.25rem', textAlign:'center' }}>
            <div style={{ position:'relative', width:64, height:64 }}>
              <div style={{ position:'absolute', inset:0, borderRadius:'50%', border:'3px solid rgba(10,186,181,0.15)', borderTopColor:'#0ABAB5', animation:'cs-spin 0.9s linear infinite' }}/>
              <div style={{ position:'absolute', inset:10, borderRadius:'50%', border:'2px solid rgba(10,186,181,0.08)', borderBottomColor:'rgba(10,186,181,0.5)', animation:'cs-spin 1.4s linear infinite reverse' }}/>
              <svg style={{ position:'absolute', inset:0, margin:'auto', display:'block' }} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            </div>
            <div style={{ fontSize:'1rem', fontWeight:800, color: S.text }}>جاري التحويل لصفحة الدفع...</div>
            <div style={{ fontSize:'0.75rem', color: S.muted, lineHeight:1.7 }}>سيتم فتح صفحة دفع آمنة<br/>وستعود تلقائياً بعد إتمام العملية</div>
            <p style={{ fontSize:'0.6rem', color: S.muted, marginTop:'0.5rem', display:'flex', alignItems:'center', gap:'0.3rem' }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              مدفوعات آمنة عبر Telr · PCI DSS Level 1
            </p>
          </div>
        )}

        {/* ══ STEP: APPLE PAY (native) ══ */}
        {step === 'apple-pay' && session && (
          <div style={{ padding:'1rem 1.1rem 2.5rem', fontFamily:"'Cairo',sans-serif" }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.25rem' }}>
              <button onClick={() => setStep('form')} style={{ ...iconBtn, fontSize:'1.3rem', fontWeight:700 }}>‹</button>
              <span style={{ fontSize:'1rem', fontWeight:900, color: S.text }}>Apple Pay</span>
              <button onClick={onClose} style={iconBtn}>✕</button>
            </div>
            <div style={{ borderRadius:'1.1rem', padding:'1.5rem', marginBottom:'1.25rem', textAlign:'center', background:'linear-gradient(135deg,#1a1a1a,#2d2d2d)', border:'1px solid rgba(255,255,255,0.1)' }}>
              <img src={applePayLogo} alt="Apple Pay" style={{ height:40, filter:'invert(1)', margin:'0 auto 0.75rem', display:'block' }}/>
              <div style={{ fontSize:'2rem', fontWeight:900, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.3rem', direction:'ltr' }}><SAR style={{ height:'0.85em', width:'auto', opacity:0.8 }}/>{amt.toFixed(2)}</div>
              <div style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.5)', marginTop:'0.25rem' }}>شحن محفظة وترلي</div>
            </div>
            <button onClick={triggerApplePay} disabled={apLoading}
              style={{ ...btnBase, background:'#000', color:'#fff', border:'1px solid rgba(255,255,255,0.15)' }}>
              {apLoading
                ? <span style={{ width:18, height:18, borderRadius:'50%', border:'2.5px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'cs-spin 0.7s linear infinite', display:'inline-block' }}/>
                : <><img src={applePayLogo} alt="" style={{ height:22, filter:'invert(1)' }}/> ادفع بـ Apple Pay</>
              }
            </button>
            <p style={{ textAlign:'center', fontSize:'0.65rem', color: S.muted, marginTop:'0.6rem' }}>يستخدم Face ID أو Touch ID للتأكيد</p>
          </div>
        )}

        {/* ══ STEP: QR ══ */}
        {step === 'qr' && session && (
          <div style={{ padding:'1rem 1.1rem 2.5rem', fontFamily:"'Cairo',sans-serif" }}>
            {/* Header */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.1rem' }}>
              <button onClick={() => { stopPolling(); setStep('form'); }} style={{ ...iconBtn, fontSize:'1.3rem', fontWeight:700 }}>‹</button>
              <div style={{ textAlign:'center' }}>
                <div style={{ fontSize:'0.6rem', color:'rgba(10,186,181,0.65)', fontWeight:700, letterSpacing:'0.1em' }}>WTRLY</div>
                <div style={{ fontSize:'0.95rem', fontWeight:900, color: S.text }}>ادفع من جوالك</div>
              </div>
              <button onClick={onClose} style={iconBtn}>✕</button>
            </div>

            {/* Amount strip */}
            <div style={{
              display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'0.75rem 1rem', borderRadius:'0.875rem', marginBottom:'1rem',
              background:'linear-gradient(135deg,rgba(10,186,181,0.12),rgba(8,145,178,0.1))',
              border:'1px solid rgba(10,186,181,0.25)',
            }}>
              <div>
                <div style={{ fontSize:'0.62rem', color: S.muted, fontWeight:700, marginBottom:'0.2rem' }}>المبلغ المطلوب</div>
                <div style={{ fontSize:'1.5rem', fontWeight:900, color:'#0ABAB5', display:'flex', alignItems:'center', gap:'0.25rem', direction:'ltr' }}>
                  <SAR style={{ height:'0.85em', width:'auto' }}/>{amt.toFixed(2)}
                </div>
              </div>
              <img src={applePayLogo} alt="Apple Pay" style={{ height:28, filter:'invert(1)', opacity:0.7 }}/>
            </div>

            {/* QR card */}
            <div style={{
              borderRadius:'1rem', overflow:'hidden',
              border:'1px solid var(--wt-border)',
              marginBottom:'0.875rem',
              boxShadow:'0 4px 20px rgba(0,0,0,0.2)',
            }}>
              {/* QR header */}
              <div style={{
                padding:'0.7rem 1rem', display:'flex', alignItems:'center', gap:'0.5rem',
                background:'rgba(10,186,181,0.08)', borderBottom:'1px solid var(--wt-border)',
              }}>
                <div style={{ width:28, height:28, borderRadius:'0.5rem', background:'rgba(10,186,181,0.15)', border:'1px solid rgba(10,186,181,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="2" strokeLinecap="round">
                    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                    <path d="M14 14h3v3m0 4h4v-4m-4 0h-3"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize:'0.78rem', fontWeight:800, color: S.text }}>امسح الرمز بكاميرا الجوال</div>
                  <div style={{ fontSize:'0.62rem', color: S.muted }}>سيُفتح نموذج الدفع على هاتفك</div>
                </div>
              </div>

              {/* QR code */}
              <div style={{
                background:'#fff',
                display:'flex', flexDirection:'column', alignItems:'center',
                padding:'1.5rem 1rem 1.1rem', gap:'1rem',
              }}>
                <div style={{ padding:'0.5rem', borderRadius:'0.875rem', background:'#fff', boxShadow:'0 2px 12px rgba(0,0,0,0.1)', border:'1px solid #f0f0f0' }}>
                  <QRImg url={session.payment_url} size={220}/>
                </div>
                {/* Payment logos */}
                <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.5rem 1rem', borderRadius:'2rem', background:'#f8f8f8', border:'1px solid #eee' }}>
                  {[applePayLogo, visaLogo, madaLogo, mastercardLogo].map((l,i) => (
                    <img key={i} src={l} alt="" style={{ height: i===0 ? 20 : 14, opacity:0.85 }}/>
                  ))}
                </div>
              </div>
            </div>

            {/* Polling indicator */}
            {polling ? (
              <div style={{
                display:'flex', alignItems:'center', gap:'0.65rem',
                padding:'0.75rem 1rem', borderRadius:'0.875rem',
                background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.2)',
              }}>
                <div style={{ position:'relative', width:20, height:20, flexShrink:0 }}>
                  <span style={{ position:'absolute', inset:0, borderRadius:'50%', background:'rgba(16,185,129,0.25)', animation:'cs-ping 1.2s cubic-bezier(0,0,0.2,1) infinite' }}/>
                  <span style={{ position:'absolute', inset:'4px', borderRadius:'50%', background:'#10b981' }}/>
                </div>
                <span style={{ fontSize:'0.75rem', color:'#10b981', fontWeight:700 }}>في انتظار تأكيد الدفع... يتم التحقق تلقائياً</span>
              </div>
            ) : (
              <div style={{
                display:'flex', alignItems:'center', gap:'0.5rem',
                padding:'0.65rem 1rem', borderRadius:'0.875rem',
                background:'rgba(255,255,255,0.04)', border:'1px solid var(--wt-border)',
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ color: S.muted, flexShrink:0 }}><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                <span style={{ fontSize:'0.72rem', color: S.muted }}>الرمز صالح لمدة محدودة — افتح الكاميرا للدفع الآن</span>
              </div>
            )}
          </div>
        )}

        {/* ══ STEP: SUCCESS ══ */}
        {step === 'success' && (
          <div style={{ padding:'1.5rem 1.1rem 3rem', fontFamily:"'Cairo',sans-serif", display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center' }}>
            {/* Success circle */}
            <div style={{ width:80, height:80, borderRadius:'50%', background:'rgba(16,185,129,0.12)', border:'2px solid rgba(16,185,129,0.3)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'1rem', boxShadow:'0 0 40px rgba(16,185,129,0.2)' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round"><path d="M5 13l4 4L19 7"/></svg>
            </div>
            <div style={{ fontSize:'1.3rem', fontWeight:900, color: S.text, marginBottom:'0.4rem' }}>تم الشحن بنجاح!</div>
            <div style={{ fontSize:'0.8rem', color: S.muted, marginBottom:'1.25rem' }}>تمت إضافة الرصيد إلى محفظتك</div>
            {newBalance !== null && (
              <div style={{ padding:'0.875rem 1.5rem', borderRadius:'0.875rem', background:'rgba(16,185,129,0.1)', border:'1px solid rgba(16,185,129,0.25)', marginBottom:'1rem', width:'100%' }}>
                <div style={{ fontSize:'0.65rem', color:'rgba(16,185,129,0.7)', fontWeight:700, marginBottom:'0.3rem' }}>الرصيد الجديد</div>
                <div style={{ fontSize:'2rem', fontWeight:900, color:'#10b981', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.3rem', direction:'ltr' }}><SAR style={{ height:'0.85em', width:'auto' }}/>{Number(newBalance).toFixed(2)}</div>
              </div>
            )}
            <div style={{ padding:'0.65rem 0.875rem', borderRadius:'0.75rem', background:'rgba(255,255,255,0.04)', border:'1px solid var(--wt-border)', width:'100%', marginBottom:'1.25rem' }}>
              <span style={{ fontSize:'0.78rem', color: S.muted }}>تم شحن <strong style={{ color: S.text, direction:'ltr', display:'inline-flex', alignItems:'center', gap:'0.15rem' }}><SAR style={{ height:'0.85em', width:'auto' }}/>{amt.toFixed(2)}</strong> بنجاح</span>
            </div>
            <button onClick={onClose} style={{ ...btnBase, background:'linear-gradient(135deg,#10b981,#059669)', color:'#fff', boxShadow:'0 6px 20px rgba(16,185,129,0.3)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 13l4 4L19 7"/></svg>
              حسناً
            </button>
          </div>
        )}

        {/* ══ STEP: FAIL ══ */}
        {step === 'fail' && (
          <div style={{ padding:'1.5rem 1.1rem 3rem', fontFamily:"'Cairo',sans-serif", display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center' }}>
            <div style={{ width:80, height:80, borderRadius:'50%', background:'rgba(244,63,94,0.12)', border:'2px solid rgba(244,63,94,0.3)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'1rem', boxShadow:'0 0 40px rgba(244,63,94,0.15)' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </div>
            <div style={{ fontSize:'1.3rem', fontWeight:900, color: S.text, marginBottom:'0.4rem' }}>فشل الدفع</div>
            <div style={{ fontSize:'0.8rem', color: S.muted, marginBottom:'1.5rem' }}>لم يتم خصم أي مبلغ من حسابك</div>
            <div style={{ display:'flex', gap:'0.6rem', width:'100%' }}>
              <button onClick={() => setStep('form')} style={{ ...btnBase, flex:1, background:'linear-gradient(135deg,#0ABAB5,#0891b2)', color:'#fff', boxShadow:'0 6px 20px rgba(10,186,181,0.3)' }}>المحاولة مجدداً</button>
              <button onClick={onClose} style={{ ...btnBase, flex:1, background:'rgba(255,255,255,0.07)', color: S.text, border:'1px solid var(--wt-border)' }}>إغلاق</button>
            </div>
          </div>
        )}

      </div>{/* sheet */}
      </div>{/* container/overlay */}
    </>
  );

  return visible ? ReactDOM.createPortal(content, document.body) : null;
}
